/**
 * Builds the request body for calculator `update_type: "filter"` (PRIMARY_INPUT_UPDATE_SCHEMA).
 * Property detail keys are only attached when truthy — same semantics as the modal Apply flow,
 * so clearing a field in the UI without sending null keeps prior server values.
 * For `buy` / `rent`, `property_price` / `monthly_rent` are always integers (0 when unset), never null — API schema rejects null.
 */

export type BuildReportFilterUpdateParams = {
  reportId: string;
  addressFull: string;
  addressShort: string;
  latitude: number;
  longitude: number;
  bedrooms: number;
  bathrooms: number;
  accommodates: number;
  /** buy | rent | manage */
  reportType: string;
  minNights: string | null;
  professionallyManaged: number | null;
  ratingMin: number;
  ratingMax: number;
  reviewCountMin: number | null;
  amenities: string[] | null;
  /**
   * Same as modal: for `buy` this is property price; for `rent` this is monthly rent.
   * Ignored for `manage`.
   */
  propertyPriceOrRent: number | null;
  propertyDetails: {
    reportName: string | null;
    /** Square meters; omit from payload when null */
    propertySizeSqm: number | null;
    buildYear: number | null;
    linkToProperty: string | null;
    notes: string | null;
    propertyImageB64: string | null;
    existingPropertyImageUrl?: string | null;
  };
};

export function buildReportFilterUpdatePayload(
  p: BuildReportFilterUpdateParams
): Record<string, unknown> {
  const rt = String(p.reportType).toLowerCase();

  const requestObj: Record<string, unknown> = {
    update_type: 'filter',
    id: p.reportId,
    address_full: p.addressFull,
    address_short: p.addressShort,
    latitude: p.latitude,
    longitude: p.longitude,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    accommodates: p.accommodates,
    report_type: rt,
    min_nights: p.minNights,
    professionally_managed: p.professionallyManaged,
    rating_min: p.ratingMin,
    rating_max: p.ratingMax,
    review_count_min: p.reviewCountMin,
    amenities: p.amenities,
  };

  const pr = p.propertyPriceOrRent;
  /** API schema requires integer, not null (PRIMARY_INPUT_UPDATE_SCHEMA + REPORT_TYPE_CHECK). */
  const priceOrRentInt =
    pr != null && pr !== 0 && Number.isFinite(Number(pr)) ? Math.round(Number(pr)) : 0;
  if (rt === 'buy') {
    requestObj.property_price = priceOrRentInt;
  }
  if (rt === 'rent') {
    requestObj.monthly_rent = priceOrRentInt;
  }

  const d = p.propertyDetails;
  if (d.reportName) requestObj.report_name = d.reportName;
  if (d.propertySizeSqm) requestObj.property_size_sqm = d.propertySizeSqm;
  if (d.buildYear) requestObj.year_built = d.buildYear;
  if (d.linkToProperty) requestObj.link_to_property = d.linkToProperty;
  if (d.notes) requestObj.notes = d.notes;
  if (d.propertyImageB64) requestObj.property_image_b64 = d.propertyImageB64;
  if (d.existingPropertyImageUrl) {
    requestObj.uploaded_property_image_url = d.existingPropertyImageUrl;
  }

  console.log('[REQUEST INPUT OBJECT]: ', requestObj);
  return requestObj;
}
